import {
  ArrowDownRight,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  XCircle,
  TrendingUp,
} from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { StatusPill } from '@/components/ui/StatusPill';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { useDashboard } from '@/data/api';
import { formatRelative, maskAccount, formatKes } from '@/lib/format';
import { REQUEST_TYPE_LABEL, type RequestType } from '@/types/request';
import { cn } from '@/lib/cn';

export function DashboardPage() {
  const dashboardQuery = useDashboard();
  const data = dashboardQuery.data;

  const activity = useMemo(
    () =>
      (data?.recent ?? []).map((r) => ({
        id: `act-${r.id}`,
        actor: r.contactName ?? 'Customer',
        verb: `${r.status === 'SUBMITTED' ? 'submitted' : 'updated'} ${r.referenceNumber}`,
        target: r.location ?? '',
        at: r.updatedAt,
      })),
    [data],
  );
  const openRequests = useMemo(
    () =>
      (data?.recent ?? [])
        .filter((r) => r.status === 'SUBMITTED' || r.status === 'UNDER_REVIEW')
        .slice(0, 10),
    [data],
  );

  if (dashboardQuery.isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }
  if (dashboardQuery.isError || !data) {
    return <ErrorState onRetry={() => dashboardQuery.refetch()} />;
  }

  const summary = {
    pendingReview: data.pendingReview,
    pendingDelta: 0,
    approvedThisWeek: data.approvedThisWeek,
    rejectedThisWeek: data.rejectedThisWeek,
    avgResolutionDays: 0,
  };
  const byType: { type: RequestType; count: number }[] = (data.countsByType ?? []).map(
    (t) => ({ type: t.type as RequestType, count: t.count }),
  );

  return (
    <div className="flex flex-col gap-6">
      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          icon={<Clock className="h-5 w-5" />}
          label="Pending review"
          value={summary.pendingReview}
          delta={`+${summary.pendingDelta} today`}
          tone="navy"
        />
        <Kpi
          icon={<CheckCircle2 className="h-5 w-5" />}
          label="Approved this week"
          value={summary.approvedThisWeek}
          delta="+8% vs last week"
          deltaTone="up"
          tone="success"
        />
        <Kpi
          icon={<XCircle className="h-5 w-5" />}
          label="Rejected this week"
          value={summary.rejectedThisWeek}
          delta="-12% vs last week"
          deltaTone="down"
          tone="error"
        />
        <Kpi
          icon={<TrendingUp className="h-5 w-5" />}
          label="Avg. resolution"
          value={`${summary.avgResolutionDays} d`}
          delta="−4 h vs last week"
          deltaTone="down"
          tone="info"
        />
      </div>

      {/* Activity + by-type */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" padding="none">
          <div className="px-5 pt-5">
            <CardHeader title="Recent activity" description="The last 18 things that happened across the team." />
          </div>
          <ul className="divide-y divide-line-soft">
            {activity.map((a) => (
              <li key={a.id} className="flex items-center gap-3 px-5 py-3">
                <Avatar name={a.actor} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="text-body text-ink truncate">
                    <span className="font-semibold">{a.actor}</span> {a.verb}
                  </div>
                  <div className="text-caption text-ink-muted truncate">{a.target}</div>
                </div>
                <div className="text-caption text-ink-muted whitespace-nowrap">
                  {formatRelative(a.at)}
                </div>
              </li>
            ))}
          </ul>
        </Card>

        <Card padding="none">
          <div className="px-5 pt-5">
            <CardHeader title="Requests by type" description="Distribution across the 12 service types." />
          </div>
          <div className="px-5 pb-5 space-y-2.5">
            {byType.map((row) => {
              const max = Math.max(...byType.map((r) => r.count));
              return (
                <div key={row.type}>
                  <div className="flex items-center justify-between text-caption text-ink-muted mb-0.5">
                    <span className="text-ink">{REQUEST_TYPE_LABEL[row.type]}</span>
                    <span className="tabular">{row.count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-canvas">
                    <div
                      className="h-2 rounded-full bg-brand-blue"
                      style={{ width: `${(row.count / max) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Oldest open requests */}
      <Card padding="none">
        <div className="px-5 pt-5">
          <CardHeader
            title="Oldest open requests"
            description="Sorted by submission time — work these first."
            actions={
              <Link
                to="/requests"
                className="text-caption font-semibold text-brand-blue hover:underline"
              >
                View all
              </Link>
            }
          />
        </div>
        <table className="w-full text-body">
          <thead>
            <tr className="text-overline text-ink-hint uppercase">
              <th className="text-left px-5 py-2">Ref</th>
              <th className="text-left py-2">Type</th>
              <th className="text-left py-2">Customer</th>
              <th className="text-left py-2">Location</th>
              <th className="text-left py-2">Submitted</th>
              <th className="text-left py-2">Status</th>
              <th className="text-right pr-5 py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {openRequests.map((r) => (
              <tr key={r.id} className="border-t border-line-soft hover:bg-canvas-hover transition-colors">
                <td className="px-5 py-2.5 font-mono text-caption">{r.referenceNumber}</td>
                <td className="py-2.5">{REQUEST_TYPE_LABEL[r.requestType]}</td>
                <td className="py-2.5">
                  <div>{r.contactName}</div>
                  <div className="text-caption text-ink-muted font-mono">{maskAccount(r.accountNumber)}</div>
                </td>
                <td className="py-2.5">{r.location ?? '—'}</td>
                <td className="py-2.5 text-ink-muted">{formatRelative(r.submittedAt)}</td>
                <td className="py-2.5"><StatusPill status={r.status} /></td>
                <td className="pr-5 py-2.5 text-right font-mono tabular">
                  {r.totalAmount ? `KES ${formatKes(r.totalAmount)}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function Kpi({
  icon,
  label,
  value,
  delta,
  deltaTone,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  delta?: string;
  deltaTone?: 'up' | 'down';
  tone: 'navy' | 'success' | 'error' | 'info';
}) {
  const toneClass = {
    navy: 'text-navy',
    success: 'text-status-success',
    error: 'text-status-error',
    info: 'text-brand-blue',
  }[tone];
  const bgTone = {
    navy: 'bg-status-info-bg',
    success: 'bg-status-success-bg',
    error: 'bg-status-error-bg',
    info: 'bg-status-info-bg',
  }[tone];

  return (
    <Card>
      <div className="flex items-start justify-between">
        <div className="text-caption text-ink-muted">{label}</div>
        <div className={cn('h-8 w-8 rounded-lg flex items-center justify-center', bgTone, toneClass)}>
          {icon}
        </div>
      </div>
      <div className={cn('mt-2 text-display tabular', toneClass)}>{value}</div>
      {delta && (
        <div className="mt-1 inline-flex items-center gap-1 text-caption text-ink-muted">
          {deltaTone === 'up' && <ArrowUpRight className="h-3.5 w-3.5 text-status-success" />}
          {deltaTone === 'down' && <ArrowDownRight className="h-3.5 w-3.5 text-status-success" />}
          <span>{delta}</span>
        </div>
      )}
    </Card>
  );
}
